import { computed, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { TypeCampaignEnum } from '@/types';
import type { FlowSettings, FlowSettingsAutomation, FlowSettingsBulk } from '@/types/flow-settings';

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string[]>;
  firstErrorField: string | null;
}

/**
 * Composable for flow settings validation using Vuelidate
 * Provides centralized validation rules for both Automation and Bulk settings
 */
export function useFlowSettingsValidation(
  settings: Ref<FlowSettings | null>,
  flowType: Ref<TypeCampaignEnum>,
) {
  const { t } = useI18n();

  // Automation-specific rules
  const automationRules = computed(() => ({
    name: {
      required: helpers.withMessage(
        () => t('automatedScenarios.globalSettings.name.required'),
        required,
      ),
    },
  }));

  // Bulk-specific rules
  const bulkRules = computed(() => ({
    name: {
      required: helpers.withMessage(
        () => t('automatedScenarios.globalBulkSettings.name.required'),
        required,
      ),
    },
  }));

  // Select rules based on flow type
  const rules = computed(() => {
    if (flowType.value === TypeCampaignEnum.AUTOMATION) {
      return automationRules.value;
    }
    return bulkRules.value;
  });

  // Create Vuelidate instance
  const v$ = useVuelidate(rules, settings as Ref<FlowSettings>);

  /**
   * Validate all fields and return validation result
   */
  const validate = async (): Promise<ValidationResult> => {
    const isValid = await v$.value.$validate();
    
    const errors: Record<string, string[]> = {};
    let firstErrorField: string | null = null;

    if (!isValid) {
      for (const key of Object.keys(v$.value)) {
        if (key.startsWith('$')) continue;
        
        const field = v$.value[key];
        if (field.$errors && field.$errors.length > 0) {
          errors[key] = field.$errors.map((e: any) => e.$message);
          if (!firstErrorField) {
            firstErrorField = key;
          }
        }
      }
    }

    return {
      isValid,
      errors,
      firstErrorField,
    };
  };

  /**
   * Reset validation state
   */
  const resetValidation = () => {
    v$.value.$reset();
  };

  /**
   * Check if a specific field has errors
   */
  const hasFieldError = (fieldName: string): boolean => {
    return v$.value[fieldName]?.$error ?? false;
  };

  /**
   * Get error messages for a specific field
   */
  const getFieldErrors = (fieldName: string): string[] => {
    if (!v$.value[fieldName]?.$errors) return [];
    return v$.value[fieldName].$errors.map((e: any) => e.$message);
  };

  return {
    validate,
    resetValidation,
    hasFieldError,
    getFieldErrors,
  };
}
